import { z } from 'zod';
import { BaseErrorSchema } from '../../shared/error.schema';
import { CommittedRowSchema } from '../../shared/bulk-commit.schema';
import { ObjectIdSchema } from '../../shared/object-id.schema';
/**
 * Initialize OrgFunction Input Schema
 * One row of the Funktionen or Teams upload. The row carries NO kind —
 * kind is fixed by the surface (ADR 0004): rows sent in `functions` become
 * role-kind, rows in `teams` become team-kind.
 */ export const InitializeOrgFunctionInputSchema = z.strictObject({
    /**
   * Present on an in-place edit of an already-imported OrgFunction: the row
   * updates that entity (rename/edit) instead of creating a new one, so a
   * user's Stelle (mainRole → this id) follows the rename. Absent = create.
   */ _id: z.string().optional(),
    name: z.string().min(1),
    description: z.string().optional(),
    /** Department abbreviation, name, or ObjectId; empty = org-wide */ department: z.string().optional(),
    /** Funktionsleiter, resolved to a user by email */ leadEmail: z.email().optional(),
    /** Shared mailbox of the Funktion/Team */ email: z.email().optional()
});
/**
 * Initialize Membership Input Schema
 * Flat row mirroring the Mitgliedschaften sheet (AD-group exports paste as
 * rows) so per-row errors map 1:1 back to what the user sees.
 */ export const InitializeMembershipInputSchema = z.strictObject({
    orgFunctionName: z.string().min(1),
    memberEmail: z.email()
});
/**
 * Initialize Membership Removal Schema
 * Keyed by OrgFunction id, not name: names resolve case-insensitively and can
 * be renamed between the link and the unlink, and a role and a team may share
 * a name — an id pins the exact link to sever.
 */ export const InitializeMembershipRemovalSchema = z.strictObject({
    orgFunctionId: ObjectIdSchema,
    memberEmail: z.email()
});
/**
 * Initialize Add OrgFunctions Request Schema
 * POST /api/v2/initialize/org-functions
 * Re-enterable: rows whose (name, kind) already exist are skipped — their
 * memberships still link.
 */ export const InitializeAddOrgFunctionsRequestSchema = z.object({
    functions: z.array(InitializeOrgFunctionInputSchema),
    teams: z.array(InitializeOrgFunctionInputSchema),
    memberships: z.array(InitializeMembershipInputSchema),
    /**
   * Ids of already-imported OrgFunctions (functions or teams) to soft-delete.
   * Rejected for any id that is a user's Hauptfunktion (mainRole); otherwise
   * the entity is soft-deleted and pulled from all users' Zusatzfunktionen.
   */ removals: z.array(ObjectIdSchema).optional(),
    /**
   * Memberships to unlink: pulls that OrgFunction from that user's
   * Zusatzfunktionen (secondaryRoles).
   */ membershipRemovals: z.array(InitializeMembershipRemovalSchema).optional()
});
/**
 * Initialize Add OrgFunctions Response Schema
 * One result per request `functions`/`teams` row, in request order. Rows sent
 * with `_id` (in-place edits) and skipped rows both echo their entity's id
 * with `created: false`, so the wizard can edit skipped rows in place.
 */ export const InitializeAddOrgFunctionsResponseSchema = z.object({
    functions: z.array(CommittedRowSchema),
    teams: z.array(CommittedRowSchema),
    memberships: z.object({
        linked: z.number(),
        skipped: z.number()
    })
});
export const InitializeAddOrgFunctionsErrorSchema = BaseErrorSchema;

//# sourceMappingURL=initialize-add-org-functions.schema.js.map